'use client';
import axios from 'axios';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import BaseButtonWithProgress from '@components/BaseButtonWithProgress';
import { ToutContent } from '@repo/ui/src/components/ToutContent';
import Section from '@repo/ui/src/components/Section';
import FormGroup from '../form-group';
import FormAcknowledgement from '../form-acknowledgement';
import { MetaProps } from '@repo/ui/src/types/meta';
import { LinkProps } from '@repo/ui/src/types/link';
import { productRegistered } from '@repo/ui/src/services/analytics';
import { identify } from '@repo/ui/src/utils/identify';
import { FormGrid, Form, Jumbotron } from './styles';
import { useEmailSignup } from '@hooks/customer/useEmailSignup';
import Script from 'next/script';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';

const findAlignment = (submitLocation: string) => {
  if (submitLocation === 'text-left') {
    return 'flex-start';
  }
  if (submitLocation === 'text-right') {
    return 'flex-end';
  }
  if (submitLocation === 'text-center') {
    return 'center';
  }
  if (['center', 'flex-end'].includes(submitLocation)) {
    return submitLocation;
  }
  return 'flex-start';
};

interface AckProps {
  acknowledgementText: string;
  emailSignup: boolean;
  smsSignup: boolean;
  preCheck: boolean;
  formLink?: Array<LinkProps>;
  showCheckbox: boolean;
  required?: boolean;
  link: LinkProps;
  disclaimer?: string;
}
interface FormGroupProps {
  groupNumber: any;
  multiSelect?: boolean;
  reguired?: boolean;
  title?: string;
  type?: string;
  validationMessage?: string;
  _meta: MetaProps;
}

interface JsonFormProps {
  _meta: MetaProps;
  destination: string;
  formFields: Array<FormGroupProps>;
  title: string;
  description: string;
  submitLocation: string;
  submitText: string;
  successTitle: string;
  formAcknowledgements: Array<AckProps>;
  successMessage: string;
  successCTA: string;
  successLink: Array<LinkProps>;
  layout: string;
  reCaptcha: {
    errorMessage: string;
    formId: string;
    threshold: string;
    _meta: MetaProps;
  };
}

const JsonFormBody = ({
  _meta,
  destination,
  formFields,
  title,
  description,
  submitLocation,
  submitText,
  formAcknowledgements,
  successTitle,
  successMessage,
  successCTA,
  successLink,
  layout,
  reCaptcha,
}: JsonFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const siteKey = publicRuntimeConfig.reCaptcha.sitekey;
  const submitAlign = findAlignment(submitLocation);
  const notOneCol = layout !== 'One Column';
  const { trigger } = useEmailSignup();

  // build the form groups
  const formGroups: any = [];
  formFields?.forEach((field: FormGroupProps) => {
    const groupNum = parseInt(field?.groupNumber || 0, 10);
    if (formGroups[groupNum]) {
      formGroups[groupNum].push(field);
    } else {
      formGroups[groupNum] = [field];
    }
  });

  // build acknowledgements
  const acks = formAcknowledgements?.map((ack: AckProps, idx) => {
    const ackItem: any = {};
    ackItem.unique = `${_meta.name}-ack-${idx}`;
    if (ack.showCheckbox) {
      ackItem.title = ack.acknowledgementText;
      ackItem.display = true;
      ackItem.preCheck = ack.preCheck;
      ackItem.emailSignup = ack.emailSignup;
      ackItem.smsSignup = ack.smsSignup;
      ackItem.disclaimer = ack.disclaimer;
    } else {
      ackItem.title = ack.acknowledgementText;
    }
    ackItem.required = ack.required;
    ackItem.link = ack.link;
    return ackItem;
  });

  const checkReCaptcha = () => {
    if (!reCaptcha) return true;
    return new Promise((resolve) => {
      grecaptcha.ready(async () => {
        const token = await grecaptcha.execute(siteKey!, { action: reCaptcha.formId });

        const result = await axios.post('/api/forms/recaptcha', { token });
        if (result.data) {
          if (
            !(
              result.data.success &&
              result.data.score > Number(reCaptcha.threshold) &&
              result.data.action === reCaptcha.formId
            )
          ) {
            setErrorMessage(reCaptcha.errorMessage);
            return resolve(false);
          }
          return resolve(true);
        }
      });
    });
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    setSubmitted(true);
    setIsLoading(true);
    setErrorMessage('');
    try {
      if (!form.checkValidity()) {
        setIsLoading(false);
        return false;
      }

      const passedReCaptcha = await checkReCaptcha();
      if (!passedReCaptcha) {
        setIsLoading(false);
        return false;
      }

      const parsedFormData = new FormData(form);
      const formDataObj: Record<string, any> = {};
      parsedFormData.forEach((value, key) => {
        formDataObj[key] = value;
      });

      const response = await axios.post(`/api/forms/process`, parsedFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 10000,
        validateStatus: (status) => status < 500,
      });
      if (!response.data) {
        throw new Error('No response from form submission');
      }
      if (_meta.name?.includes('register-your-grill')) {
        const email = String(parsedFormData.get('email')).toLowerCase();
        const first_name = String(parsedFormData.get('first_name'));
        const last_name = String(parsedFormData.get('last_name'));
        const city = String(parsedFormData.get('city'));
        const state = String(parsedFormData.get('state'));
        const phoneNumber = String(parsedFormData.get('phone_number'));
        const emailCheckbox = acks?.find((item) => item.emailSignup);
        const emailCheckState = parsedFormData.get(emailCheckbox.unique);
        const emailSubscribe = emailCheckState === 'on' && !!email;
        const smsCheckbox = acks?.find((item) => item.smsSignup);
        const smsCheckState = parsedFormData.get(smsCheckbox.unique);
        const smsSubscribe = smsCheckState === 'on' && !!phoneNumber;
        trigger({
          email,
          sourceDetail: 'product_registration',
          firstName: first_name,
          lastName: last_name,
          phoneNumber,
          emailSubscribe,
          smsSubscribe,
        });
        identify({ email, first_name, last_name, address: [city, state] });
        productRegistered({
          email,
          first_name,
          last_name,
          grill_uuid: String(parsedFormData.get('grill_uuid__applies_to_d2_wifire_grills_only_')),
          grill_serial_number: String(parsedFormData.get('serial_number')),
          registered_product: String(parsedFormData.get('grill_model')),
        });
      }

      setErrorMessage('');
      setIsSuccess(true);
    } catch (error) {
      setErrorMessage('Error processing submission. Please try again later.');
    } finally {
      setIsLoading(false);
    }

    return false;
  };

  const sCTA = (cta: any, link: LinkProps) => {
    if (cta) {
      return (
        <Box my={5} mx="auto">
          <Button variant="contained" color="primary" href={`/${link.link}`}>
            {cta}
          </Button>
        </Box>
      );
    }
    return null;
  };

  return (
    <Jumbotron py={2}>
      <Script src={`https://www.google.com/recaptcha/api.js?render=${siteKey}`} />
      <Section gutterX gutterY>
        <Grid container py={2} direction="row" justifyContent="center">
          {!isSuccess && (
            <Grid item xs={12} md={8}>
              <Box mt={1}>
                <ToutContent
                  align="left"
                  heading={title}
                  headingStyleVariant="h1"
                  description={description}
                  divider
                />
              </Box>
            </Grid>
          )}
          <FormGrid item sm={8}>
            <Box>
              <Form className={`needs-validation`} onSubmit={onSubmit} noValidate>
                {!isSuccess && (
                  <Grid container direction={notOneCol ? 'column' : 'row'} display="block">
                    {notOneCol ? (
                      <Grid container direction="row" item mx={0}>
                        {formGroups
                          ? formGroups.map((group: number, idx: number) => (
                              <FormGroup
                                items={group}
                                key={`formgroup-${idx}`}
                                col={formGroups.length}
                                layout={layout}
                                submitted={submitted}
                              />
                            ))
                          : null}
                      </Grid>
                    ) : (
                      <Box
                        display={notOneCol ? 'flex' : 'block'}
                        flexDirection={notOneCol ? 'column' : undefined}
                      >
                        {formGroups &&
                          formGroups.map((group: number, idx: number) => (
                            <FormGroup
                              items={group}
                              key={`formgroup-${idx}`}
                              col={formGroups.length}
                              layout={layout}
                              submitted={submitted}
                            />
                          ))}
                      </Box>
                    )}
                    {acks
                      ? acks.map((ack) => (
                          <FormAcknowledgement
                            item={ack}
                            submitted={submitted}
                            unique={ack.unique}
                            key={`ack-${ack.unique}`}
                            layout={layout}
                            preCheck={ack.preCheck}
                          />
                        ))
                      : null}
                    <Grid item container alignItems={submitAlign || (notOneCol ? '' : 'center')}>
                      <Box>
                        <Input type="hidden" name="form_name" value={`${_meta.name}`} />
                        <Input
                          type="hidden"
                          name="form_destination"
                          value={destination || 'Database Only'}
                        />
                        <BaseButtonWithProgress
                          type="submit"
                          processing={isLoading}
                          processingText="submitting"
                        >
                          {submitText}
                        </BaseButtonWithProgress>
                        {submitted && errorMessage && (
                          <Box className="invalid-feedback" display="block">
                            {errorMessage}
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                )}
                {isSuccess && (
                  <Box height="30vh" justifyContent="center">
                    <Grid container direction="row" justifyContent="center" alignItems="center">
                      <Grid item xs={6}>
                        <Typography variant="h3">{successTitle || 'Success'}</Typography>
                        {successMessage ? <p>{successMessage}</p> : null}
                        {successCTA && successLink && successLink.length > 0
                          ? sCTA(successCTA, successLink[0])
                          : null}
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Form>
            </Box>
          </FormGrid>
        </Grid>
      </Section>
    </Jumbotron>
  );
};

export default JsonFormBody;
